export interface EmailAuthHint {
  exists: boolean;
  /** Provider ids linked to the account, e.g. ['google'] or ['email']. */
  providers: string[];
  /** True when the account can sign in with an email + password. */
  hasPassword: boolean;
}

export const EMPTY_EMAIL_AUTH_HINT: EmailAuthHint = {
  exists: false,
  providers: [],
  hasPassword: false,
};

/**
 * Ask the server which sign-in methods an email is registered with. Existence
 * is CANONICAL-AWARE server-side (gmail dot/plus + RFC subaddress collapse to
 * one identity — BAL-718), so a variant address resolves to the underlying
 * account.
 *
 * Never throws: any transport/parse failure degrades to "no hint" so the caller
 * (recovery hint, or the signup identity guard) fails OPEN — it only ever
 * *enriches* or *pre-empts*, and must not lock a user out on infra trouble.
 */
export async function fetchEmailAuthHint(
  email: string,
): Promise<EmailAuthHint> {
  try {
    const response = await fetch('/api/auth/email-hint', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      return EMPTY_EMAIL_AUTH_HINT;
    }

    return (await response.json()) as EmailAuthHint;
  } catch {
    return EMPTY_EMAIL_AUTH_HINT;
  }
}
